import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

export interface Button {
  label: string;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'base' | 'lg';
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  iconOnly?: boolean;
  hideLabel?: boolean;
  hidden?: boolean;
  onClick: () => void;
}

@Component({
  selector: 'ui-button',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    @if (!hidden) {
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [class]="getButtonClasses()"
      (click)="onClick.emit($event)"
    >
      <span class="flex items-center gap-2">
        <ui-icon
          *ngIf="loading"
          name="spinner"
          class="animate-spin"
          [size]="size === 'sm' ? 'sm' : 'base'"
          [class]="getIconColorClass()"
        />
        <ui-icon
          *ngIf="icon && !loading"
          [name]="icon"
          [size]="size === 'sm' ? 'sm' : 'base'"
          [class]="getIconColorClass()"
        />
        <span *ngIf="!hideLabel">
          <ng-content></ng-content>
        </span>
      </span>
    </button>
    }
  `,
})
export class ButtonComponent {
  @Input() variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
  @Input() size?: 'sm' | 'base' | 'lg';
  @Input() type?: 'button' | 'submit';
  @Input() disabled?: boolean;
  @Input() loading?: boolean;
  @Input() icon?: string;
  @Input() hideLabel?: boolean;
  @Input() iconOnly?: boolean = false;
  @Input() hidden?: boolean = false;
  @Output() onClick = new EventEmitter<MouseEvent>();

  getButtonClasses(): string {
    const baseClasses =
      'inline-flex items-center justify-center font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors';

    const sizeClasses = {
      sm: 'px-2.5 py-1.5 text-xs',
      base: 'px-3 py-2 text-sm',
      lg: 'px-4 py-2 text-base',
    }[this.size || 'base'];

    if (this.iconOnly) {
      const variantClasses = {
        primary:
          'text-primary-600 hover:border hover:border-primary-600 disabled:text-primary-300 focus:ring-0',
        secondary:
          'text-slate-700 hover:border hover:border-slate-300 disabled:text-slate-300 focus:ring-0',
        danger:
          'text-danger-600 hover:border hover:border-danger-600 disabled:text-danger-300 focus:ring-0',
        ghost:
          'text-slate-700 hover:border hover:border-slate-300 disabled:text-slate-300 focus:ring-0',
        success:
          'text-success-600 hover:border hover:border-success-600 disabled:text-success-300 focus:ring-0',
      }[this.variant || 'primary'];

      return `${baseClasses} ${sizeClasses} ${variantClasses}`;
    }

    const variantClasses = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 disabled:bg-primary-300',
      secondary:
        'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 focus:ring-slate-500 disabled:bg-slate-100',
      danger:
        'bg-danger-600 text-white hover:bg-danger-700 focus:ring-danger-500 disabled:bg-danger-300',
      ghost:
        'text-slate-700 hover:bg-slate-100 focus:ring-slate-500 disabled:text-slate-300',
      success:
        'bg-success-600 text-white hover:bg-success-700 focus:ring-success-500 disabled:bg-success-300',
    }[this.variant || 'primary'];

    return `${baseClasses} ${sizeClasses} ${variantClasses}`;
  }

  getIconColorClass(): string {
    if (!this.iconOnly) return '';

    return {
      primary:
        'text-primary-600 hover:text-primary-700 disabled:text-primary-300',
      secondary: 'text-slate-700 hover:text-slate-800 disabled:text-slate-300',
      danger: 'text-danger-600 hover:text-danger-700 disabled:text-danger-300',
      ghost: 'text-slate-700 hover:text-slate-800 disabled:text-slate-300',
      success: 'text-success-600 hover:text-success-700 disabled:text-success-300',
    }[this.variant || 'primary'];
  }
}
